import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, authAPI, clientsAPI, licenseAPI } from '../services/api'; // Changed from '../services/authService' to match api.js export
import { toast } from 'react-hot-toast';

const BROADCAST_CHANNEL_NAME = 'pom_auth_channel';
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activePortal, setActivePortal] = useState(() => localStorage.getItem('activePortal') || 'client');
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [supportSessionTenant, setSupportSessionTenant] = useState(() => {
    try {
      const stored = localStorage.getItem('support_session_tenant');
      return stored ? JSON.parse(stored) : null;
    } catch (_) {
      return null;
    }
  });

  const isSuperAdmin = React.useMemo(() => {
    if (!user) return false;
    const roles = Array.isArray(user.role) ? user.role : (Array.isArray(user.roles) ? user.roles.map(r => r?.name || r) : [user.role].filter(Boolean));
    return roles.includes('SuperAdmin') || user.type === 'SuperAdmin' || user.email === 'connect@sanglob.com' || user.email === 'superadmin@sanglob.local';
  }, [user]);

  const startSupportSession = useCallback((tenant, reason = 'Support Investigation') => {
    setSupportSessionTenant(tenant);
    try {
      localStorage.setItem('support_session_tenant', JSON.stringify(tenant));
      localStorage.setItem('support_session_reason', reason);
      localStorage.setItem('selectedClientId', String(tenant.id));
    } catch (_) {}
    navigate('/dashboard');
  }, [navigate]);

  const endSupportSession = useCallback(async () => {
    try {
      if (supportSessionTenant?.id) {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        await fetch(`${api.defaults.baseURL}/support/end`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ tenantId: supportSessionTenant.id })
        });
      }
    } catch (e) {
      console.error('Error logging support session end', e);
    }
    setSupportSessionTenant(null);
    try {
      localStorage.removeItem('support_session_tenant');
      localStorage.removeItem('support_session_reason');
      localStorage.removeItem('selectedClientId');
    } catch (_) {}
    navigate('/settings/control-tower');
  }, [navigate, supportSessionTenant]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);
  const activityHandlerRef = useRef(null);

  const sanitizeClientId = (value) => {
    const s = String(value ?? '').trim()
    if (!/^\d+$/.test(s)) return ''
    const n = Number(s)
    return n > 0 ? s : ''
  }

  const ensureSelectedClientId = useCallback(async () => {
    try {
      if (activePortal !== 'client') return
      const userTenantId = user?.tenant_id || user?.client_id
      if (userTenantId && !isSuperAdmin) {
        localStorage.setItem('selectedClientId', String(userTenantId))
        return
      }
      const existing = sanitizeClientId(localStorage.getItem('selectedClientId'))
      if (existing) return
      if (isSuperAdmin) {
        const res = await clientsAPI.getAll()
        const list = res?.data?.data?.clients || res?.data?.data || []
        const active = Array.isArray(list) ? list.filter((c) => c.status === 'Active') : []
        if (active.length > 0 && active[0]?.id) {
          localStorage.setItem('selectedClientId', String(active[0].id))
        }
      }
    } catch (_) { void 0 }
  }, [activePortal, user, isSuperAdmin])

  const switchPortal = (portal) => {
    if (portal !== 'client' && portal !== 'vendor') return;
    setActivePortal(portal);
    localStorage.setItem('activePortal', portal);
    toast.success(`Switched to ${portal === 'client' ? 'Client' : 'Vendor'} Portal`);
  };

  const logout = useCallback(async (isRemote = false) => {
    const roles = Array.isArray(user?.role) ? user.role : (Array.isArray(user?.roles) ? user.roles.map(r => r?.name || r) : [user?.role].filter(Boolean));
    const wasSuperAdmin = isSuperAdmin || user?.type === 'SuperAdmin' || roles.includes('SuperAdmin') || user?.email === 'connect@sanglob.com' || activePortal === 'superadmin' || window.location.pathname.startsWith('/superadmin');
    const targetLoginRoute = wasSuperAdmin ? '/superadmin/login' : '/login';

    try {
      if (!isRemote) {
        // Call backend to invalidate session
        await authAPI.logout(); 
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('activePortal');
      localStorage.removeItem('selectedClientId');
      sessionStorage.removeItem('token');
      setUser(null);
      setActivePortal('client');
      if (!isRemote) {
        toast.success('Logged out successfully');
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/superadmin/login')) {
          navigate(targetLoginRoute);
        }
      } else {
        // Remote logout (e.g. from another tab or idle)
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/superadmin/login')) {
          navigate(targetLoginRoute);
        }
      }
    }
  }, [navigate, user, isSuperAdmin, activePortal]);

  useEffect(() => {
    // Safety timeout to ensure app doesn't get stuck in loading state
    const safetyTimeout = setTimeout(() => {
      setLoading((currentLoading) => {
        if (currentLoading) {
          console.warn('Auth initialization timed out safely, forcing app load');
          return false;
        }
        return currentLoading;
      });
    }, 7000);

    const initAuth = async () => {
      console.log('initAuth started');
      try {
        const localToken = localStorage.getItem('token')
        const sessionToken = sessionStorage.getItem('token')
        if (localToken && !sessionToken) {
          try {
            sessionStorage.setItem('token', localToken)
          } catch (_) { void 0 }
        }
        const token = sessionStorage.getItem('token') || localStorage.getItem('token')
        
        // Always check license status first or in parallel
        try {
          console.log('Checking license status...');
          // Add a timeout to prevent hanging indefinitely
          const licensePromise = licenseAPI.getStatus();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('License check timed out')), 5000)
          );
          
          const licenseRes = await Promise.race([licensePromise, timeoutPromise]);
          console.log('License check response:', licenseRes);
          
          if (licenseRes.data) {
             const licData = licenseRes.data?.data || licenseRes.data;
             setLicenseStatus(licData);
          }
        } catch (err) {
          console.error('Failed to fetch license status:', err);
          // Default to invalid if check fails, but don't block the UI
          setLicenseStatus({ valid: false, error: 'Connection Error', status: 'READ_ONLY_EXPIRED' });
        }

        if (token) {
          try {
            console.log('Checking auth token...');
            const response = await authAPI.getMe();
            console.log('Auth check response:', response);
            setUser(response.data);
            try {
              const scopedLicense = await licenseAPI.getStatus(response.data?.tenant_id);
              if (scopedLicense?.data) {
                setLicenseStatus(scopedLicense.data);
              }
            } catch (_) { void 0 }
            if (response.data?.tenant_id && response.data?.type !== 'SuperAdmin') {
              localStorage.setItem('selectedClientId', String(response.data.tenant_id));
            }
            await ensureSelectedClientId();
          } catch (error) {
            console.error('Auth initialization failed:', error);
            // Only clear token and reset user if server explicitly returns 401 or 403
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
              localStorage.removeItem('token');
              sessionStorage.removeItem('token');
              setUser(null);
            }
          }
        }
      } catch (globalError) {
        console.error('Critical error in initAuth:', globalError);
      } finally {
        console.log('initAuth finished, setting loading to false');
        setLoading(false);
        clearTimeout(safetyTimeout);
      }
    };

    initAuth();
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  // Sync activePortal with user permissions
  useEffect(() => {
    if (user) {
      // Check if user is Admin - Admins can access both portals regardless of portal_access setting
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      const isAdmin = roles.includes('Admin') || roles.includes('SuperAdmin');
      
      if (isAdmin) return;

      if (user.portal_access === 'Vendor Portal' && activePortal !== 'vendor') {
        setActivePortal('vendor');
      } else if (user.portal_access === 'Client Portal' && activePortal !== 'client') {
        setActivePortal('client');
      }
    }
  }, [user, activePortal]);

  // Automatically track last visited route for seamless session restoration on refresh
  useEffect(() => {
    if (location.pathname && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/superadmin/login')) {
      const fullPath = location.pathname + (location.search || '');
      localStorage.setItem('last_visited_path', fullPath);
    }
  }, [location.pathname, location.search]);

  const login = async (email, password, rememberMe, force = false, code = null) => {
    const response = await authAPI.login({ email, password, force, code });
    const { token, ...userData } = response.data;
    
    setUser(userData);
    
    const roles = Array.isArray(userData.role) ? userData.role : (Array.isArray(userData.roles) ? userData.roles.map(r => r?.name || r) : [userData.role].filter(Boolean));
    const isSuper = userData.type === 'SuperAdmin' || roles.includes('SuperAdmin') || userData.email === 'connect@sanglob.com' || userData.email === 'superadmin@sanglob.local';

    // Set active portal and tenant scope based on access
    if (isSuper) {
        setActivePortal('superadmin');
        localStorage.setItem('activePortal', 'superadmin');
    } else if (userData.portal_access === 'Vendor Portal' || userData.type === 'Vendor') {
        setActivePortal('vendor');
        localStorage.setItem('activePortal', 'vendor');
    } else if (userData.portal_access === 'Client Portal' || userData.type === 'Client') {
        setActivePortal('client');
        localStorage.setItem('activePortal', 'client');
        if (userData.tenant_id) {
          localStorage.setItem('selectedClientId', String(userData.tenant_id));
        }
    } else {
        // Both - respect last used or default to client
        const lastUsed = localStorage.getItem('activePortal');
        if (!lastUsed || (lastUsed !== 'client' && lastUsed !== 'vendor')) {
             setActivePortal('client');
             localStorage.setItem('activePortal', 'client');
        }
        if (userData.tenant_id) {
          localStorage.setItem('selectedClientId', String(userData.tenant_id));
        }
    }
    
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);

    try {
      const scopedLicense = await licenseAPI.getStatus(userData.tenant_id)
      if (scopedLicense?.data) {
        setLicenseStatus(scopedLicense.data)
      }
    } catch (_) { void 0 }

    await ensureSelectedClientId()
    
    return response;
  };

  const verify2FA = async (tempToken, code, rememberMe = true) => {
    const response = await authAPI.verify2FA({ tempToken, code });
    if (response.data?.token) {
      const { token, ...userData } = response.data;
      setUser(userData);
      
      const roles = Array.isArray(userData.role) ? userData.role : (Array.isArray(userData.roles) ? userData.roles.map(r => r?.name || r) : [userData.role].filter(Boolean));
      const isSuper = userData.type === 'SuperAdmin' || roles.includes('SuperAdmin') || userData.email === 'connect@sanglob.com' || userData.email === 'superadmin@sanglob.local';

      if (isSuper) {
        setActivePortal('superadmin');
        localStorage.setItem('activePortal', 'superadmin');
      } else if (userData.portal_access === 'Vendor Portal' || userData.type === 'Vendor') {
        setActivePortal('vendor');
        localStorage.setItem('activePortal', 'vendor');
      } else {
        setActivePortal('client');
        localStorage.setItem('activePortal', 'client');
        if (userData.tenant_id) {
          localStorage.setItem('selectedClientId', String(userData.tenant_id));
        }
      }
      
      if (rememberMe) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
      sessionStorage.setItem('token', token);

      try {
        const scopedLicense = await licenseAPI.getStatus(userData.tenant_id);
        if (scopedLicense?.data) {
          setLicenseStatus(scopedLicense.data);
        }
      } catch (_) { void 0 }

      await ensureSelectedClientId();
    }
    return response;
  };

  const loginWithSSO = async (ssoResult, rememberMe = true) => {
    const { token, ...userData } = ssoResult;
    setUser(userData);

    const roles = Array.isArray(userData.role) ? userData.role : (Array.isArray(userData.roles) ? userData.roles.map(r => r?.name || r) : [userData.role].filter(Boolean));
    const isSuper = userData.type === 'SuperAdmin' || roles.includes('SuperAdmin') || userData.email === 'connect@sanglob.com' || userData.email === 'superadmin@sanglob.local';

    if (isSuper) {
      setActivePortal('superadmin');
      localStorage.setItem('activePortal', 'superadmin');
    } else if (userData.portal_access === 'Vendor Portal' || userData.type === 'Vendor') {
      setActivePortal('vendor');
      localStorage.setItem('activePortal', 'vendor');
    } else {
      setActivePortal('client');
      localStorage.setItem('activePortal', 'client');
      if (userData.tenant_id) {
        localStorage.setItem('selectedClientId', String(userData.tenant_id));
      }
    }

    if (rememberMe) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    sessionStorage.setItem('token', token);

    try {
      const scopedLicense = await licenseAPI.getStatus(userData.tenant_id);
      if (scopedLicense?.data) {
        setLicenseStatus(scopedLicense.data);
      }
    } catch (_) { void 0; }

    await ensureSelectedClientId();
    return userData;
  };

  const refreshMe = useCallback(async () => {
    const response = await authAPI.getMe();
    setUser(response.data);
    return response.data;
  }, []);

  useEffect(() => {
    // Initialize BroadcastChannel safely
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        channelRef.current.onmessage = (event) => {
          if (event.data.type === 'ACTIVITY') {
            activityHandlerRef.current?.(true)
          }
        };
      }
    } catch (e) {
      console.warn('BroadcastChannel not supported or failed to initialize', e);
    }

    // Listen for unauthorized API responses
    const handleUnauthorized = () => {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      setUser(null);
      const isSuper = window.location.pathname.startsWith('/superadmin') || activePortal === 'superadmin';
      const target = isSuper ? '/superadmin/login' : '/login';

      if (!window.location.pathname.startsWith('/login') && 
          !window.location.pathname.startsWith('/superadmin/login') && 
          !window.location.pathname.startsWith('/forgot-password') && 
          !window.location.pathname.startsWith('/reset-password') && 
          !window.location.pathname.startsWith('/help')) {
        navigate(target);
      }
    };
    window.addEventListener('unauthorized', handleUnauthorized);

    const handleSessionTimeout = async () => {
      try {
        await authAPI.ping()
        await refreshMe()
      } catch (_) { void 0 }
    }
    window.addEventListener('session-timeout', handleSessionTimeout)

    return () => {
      channelRef.current?.close();
      window.removeEventListener('unauthorized', handleUnauthorized);
      window.removeEventListener('session-timeout', handleSessionTimeout)
    };
  }, [logout, refreshMe]);

  // Session Activity & Idle Timeout Monitor
  useEffect(() => {
    if (!user) return () => {};

    const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes of genuine inactivity
    const PING_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes heartbeat
    const CHECK_INTERVAL_MS = 10 * 1000;    // Check every 10 seconds

    let lastActivity = Date.now();
    let lastPing = Date.now();
    try {
      localStorage.setItem('last_user_activity', String(lastActivity));
    } catch (_) { void 0; }

    const safePing = async () => {
      try {
        await authAPI.ping();
      } catch (_) { void 0; }
    };

    const registerActivity = (fromRemote = false) => {
      const now = Date.now();
      lastActivity = now;
      try {
        localStorage.setItem('last_user_activity', String(now));
      } catch (_) { void 0; }

      if (!fromRemote) {
        channelRef.current?.postMessage({ type: 'ACTIVITY', at: now });
      }

      // If it's been more than PING_INTERVAL_MS since last ping, ping backend immediately
      if (now - lastPing >= PING_INTERVAL_MS) {
        lastPing = now;
        safePing();
      }
    };

    activityHandlerRef.current = registerActivity;

    // Periodic watchdog timer checking actual idle duration across all tabs
    const watchdogInterval = setInterval(() => {
      const now = Date.now();
      let storedActivity = lastActivity;
      try {
        const item = localStorage.getItem('last_user_activity');
        if (item) storedActivity = Number(item);
      } catch (_) { void 0; }

      const mostRecentActivity = Math.max(lastActivity, storedActivity);
      const idleTime = now - mostRecentActivity;

      if (idleTime >= IDLE_TIMEOUT_MS) {
        clearInterval(watchdogInterval);
        toast.error('Session timed out due to inactivity.');
        logout();
        return;
      }

      // Periodic heartbeat keep-alive while active
      if (now - lastPing >= PING_INTERVAL_MS) {
        lastPing = now;
        safePing();
      }
    }, CHECK_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        registerActivity(false);
      }
    };

    const onActivityEvent = () => registerActivity(false);

    const passiveOpts = { passive: true };
    window.addEventListener('mousemove', onActivityEvent, passiveOpts);
    window.addEventListener('mousedown', onActivityEvent, passiveOpts);
    window.addEventListener('pointerdown', onActivityEvent, passiveOpts);
    window.addEventListener('keydown', onActivityEvent, passiveOpts);
    window.addEventListener('keypress', onActivityEvent, passiveOpts);
    window.addEventListener('click', onActivityEvent, passiveOpts);
    window.addEventListener('scroll', onActivityEvent, passiveOpts);
    window.addEventListener('wheel', onActivityEvent, passiveOpts);
    window.addEventListener('touchstart', onActivityEvent, passiveOpts);
    window.addEventListener('focus', onActivityEvent);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Initial ping on mount
    safePing();

    return () => {
      clearInterval(watchdogInterval);
      if (activityHandlerRef.current === registerActivity) activityHandlerRef.current = null;
      window.removeEventListener('mousemove', onActivityEvent);
      window.removeEventListener('mousedown', onActivityEvent);
      window.removeEventListener('pointerdown', onActivityEvent);
      window.removeEventListener('keydown', onActivityEvent);
      window.removeEventListener('keypress', onActivityEvent);
      window.removeEventListener('click', onActivityEvent);
      window.removeEventListener('scroll', onActivityEvent);
      window.removeEventListener('wheel', onActivityEvent);
      window.removeEventListener('touchstart', onActivityEvent);
      window.removeEventListener('focus', onActivityEvent);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, logout]);

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    const roles = Array.isArray(user.role) ? user.role : (Array.isArray(user.roles) ? user.roles.map(r => r?.name || r) : [user.role || '']);
    const isAdmin = isSuperAdmin || user.type === 'SuperAdmin' || user.type === 'ClientTenantAdmin' || user.type === 'TenantAdmin' || roles.some(r => ['Admin', 'SuperAdmin', 'Client Tenant Admin', 'Tenant Admin', 'Client Admin', 'TenantAdmin', 'ClientTenantAdmin', 'CEO'].includes(r));
    if (isAdmin) return true;
    
    return user.permissions?.includes(permission);
  };

  // Check if user has any of the required permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    const roles = Array.isArray(user.role) ? user.role : (Array.isArray(user.roles) ? user.roles.map(r => r?.name || r) : [user.role || '']);
    const isAdmin = isSuperAdmin || user.type === 'SuperAdmin' || user.type === 'ClientTenantAdmin' || user.type === 'TenantAdmin' || roles.some(r => ['Admin', 'SuperAdmin', 'Client Tenant Admin', 'Tenant Admin', 'Client Admin', 'TenantAdmin', 'ClientTenantAdmin', 'CEO'].includes(r));
    if (isAdmin) return true;
    
    return permissions.some(p => user.permissions?.includes(p));
  };

  // Dynamically resolve entitled/enabled modules for the current tenant
  const enabledModules = React.useMemo(() => {
    if (!user) return [];
    if (isSuperAdmin) {
      return [
        'dashboard', 'budget', 'procurement', 'buy', 'po', 'approvals', 'approval_workflow', 'sourcing', 'finance', 'inventory', 'grc', 'integration', 'platform', 'settings', 'pos', 'analytics_ai', 'help_support', 'client_portal', 'vendor_portal', 'operations', 'multi_currency', 'reporting'
      ];
    }
    if (user?.modules && Array.isArray(user.modules) && user.modules.length > 0) {
      return [...user.modules, 'approval_workflow', 'approvals'];
    }
    const licMods = licenseStatus?.data?.modules || licenseStatus?.modules || licenseStatus?.license?.modules;
    if (licMods && Array.isArray(licMods) && licMods.length > 0) {
      return [...licMods, 'approval_workflow', 'approvals'];
    }
    // Default core modules for any active tenant/client user
    return [
      'dashboard', 'budget', 'procurement', 'buy', 'po', 'approvals', 'approval_workflow', 'sourcing', 'finance', 'inventory', 'grc', 'pos', 'analytics_ai', 'help_support', 'client_portal', 'vendor_portal', 'operations', 'multi_currency', 'reporting', 'settings'
    ];
  }, [user, licenseStatus, isSuperAdmin]);

  // Centralized Scopes
  const tenantScope = React.useMemo(() => ({
    tenantId: user?.tenant_id || user?.client_id || null,
    clientCode: user?.client_code || null,
        clientName: user?.client_name || user?.tenant_name || null,
    isAssigned: Boolean(user?.tenant_id || user?.client_id)
  }), [user]);

  const licenseScope = React.useMemo(() => {
    const rawStatus = licenseStatus?.status || licenseStatus?.data?.status;
    const expiresAt = licenseStatus?.expiryDate || licenseStatus?.expiry_date || licenseStatus?.valid_until || licenseStatus?.data?.expiryDate || null;
    const isDateExpired = expiresAt ? (new Date(expiresAt) < new Date()) : false;
    const isStatusExpired = rawStatus === 'READ_ONLY_EXPIRED' || rawStatus === 'Expired' || licenseStatus?.isExpired === true;
    const isExplicitValid = licenseStatus ? Boolean(licenseStatus.valid) : true;

    const valid = isSuperAdmin ? true : (isExplicitValid && !isDateExpired && !isStatusExpired);
    const readOnly = isSuperAdmin ? false : (!valid || isStatusExpired || isDateExpired);

    return {
      licenseStatus,
      isValid: valid,
      isReadOnly: readOnly,
      editionTier: licenseStatus?.plan || licenseStatus?.plan_name || licenseStatus?.data?.plan || 'Enterprise',
      expiresAt,
      isExpired: isDateExpired || isStatusExpired
    };
  }, [licenseStatus, isSuperAdmin]);

  const isModuleEnabled = useCallback((moduleCode) => {
    if (!moduleCode) return true;
    if (isSuperAdmin) return true;
    const target = String(moduleCode).toLowerCase().trim();
    const normalized = (enabledModules || []).map(m => String(m).toLowerCase().trim());
    const ALIASES = {
      procurement: ['procurement', 'operations', 'buy', 'po', 'sourcing', 'purchasing'],
      buy: ['buy', 'procurement', 'operations', 'po', 'sourcing'],
      operations: ['operations', 'procurement', 'buy', 'po'],
      inventory: ['inventory', 'stock', 'warehouse'],
      finance: ['finance', 'gl', 'accounts', 'pay', 'accounting'],
      budget: ['budget', 'plan', 'planning'],
      approval_workflow: ['approval_workflow', 'approvals', 'workflow', 'operations', 'platform'],
      approvals: ['approval_workflow', 'approvals', 'workflow', 'operations', 'platform'],
      grc: ['grc', 'govern', 'compliance'],
      vendor_portal: ['vendor_portal', 'vendor'],
      client_portal: ['client_portal', 'client'],
      help_support: ['help_support', 'help', 'tickets', 'support'],
      pos: ['pos', 'store', 'branches'],
      multi_currency: ['multi_currency', 'fx', 'currencies'],
      analytics_ai: ['analytics_ai', 'analytics', 'dashboard', 'ai'],
      reporting: ['reporting', 'reports', 'report'],
      settings: ['settings', 'config', 'platform'],
      platform: ['platform', 'core', 'settings', 'admin']
    };
    const candidates = ALIASES[target] || [target];
    return candidates.some(c => normalized.includes(c));
  }, [enabledModules, isSuperAdmin]);

  const roleScope = React.useMemo(() => {
    const rolesList = Array.isArray(user?.role) ? user.role : (Array.isArray(user?.roles) ? user.roles.map(r => r?.name || r) : [user?.role].filter(Boolean));
    const isTenantAdmin = rolesList.includes('Admin') && !isSuperAdmin;
    const isVendor = user?.type === 'Vendor' || Boolean(user?.vendor_id) || rolesList.includes('Vendor');
    const isClient = user?.type === 'Client' || (Boolean(user?.client_id) && !isSuperAdmin && !isTenantAdmin);
    const isViewer = rolesList.includes('Viewer') && !isTenantAdmin && !isSuperAdmin;
    return {
      roles: rolesList,
      primaryRole: rolesList[0] || user?.type || 'User',
      isSuperAdmin,
      isTenantAdmin,
      isVendor,
      isClient,
      isViewer
    };
  }, [user, isSuperAdmin]);

  const isViewOnly = React.useMemo(() => {
    if (isSuperAdmin) return false;
    if (roleScope.isViewer) return true;
    return Boolean(licenseScope.isReadOnly);
  }, [isSuperAdmin, roleScope.isViewer, licenseScope.isReadOnly]);

  const permissionScope = React.useMemo(() => ({
    permissions: user?.permissions || [],
    hasPermission: (perm) => {
      if (isSuperAdmin) return true;
      if (!perm) return true;
      return Boolean(user?.permissions?.includes(perm));
    },
    hasAnyPermission: (perms = []) => {
      if (isSuperAdmin) return true;
      if (!perms || !perms.length) return true;
      return perms.some(p => user?.permissions?.includes(p));
    }
  }), [user, isSuperAdmin]);

  const dataScope = React.useMemo(() => ({
    branchId: user?.branch_id || null,
    storeId: user?.store_id || null,
    isMultiBranch: Boolean(user?.is_multi_branch)
  }), [user]);

  const isAiEnabled = React.useMemo(() => {
    if (isSuperAdmin) return true;
    return Boolean(user?.is_ai_enabled);
  }, [user, isSuperAdmin]);

  const isAlertsEnabled = React.useMemo(() => {
    if (isSuperAdmin) return true;
    return Boolean(user?.is_alerts_enabled);
  }, [user, isSuperAdmin]);

  const value = {
    user,
    isSuperAdmin,
    isTenantAdmin: roleScope.isTenantAdmin,
    isViewer: roleScope.isViewer,
    isAiEnabled,
    isAlertsEnabled,
    activePortal,
    switchPortal,
    licenseStatus,
    isLicenseReadOnly: Boolean(licenseScope?.isReadOnly),
    isLicenseExpired: Boolean(licenseScope?.isExpired),
    isViewOnly,
    canCreate: (resource) => !isViewOnly && (permissionScope.hasPermission(`${resource}:create`) || permissionScope.hasPermission(`${resource}:manage`)),
    canUpdate: (resource) => !isViewOnly && (permissionScope.hasPermission(`${resource}:update`) || permissionScope.hasPermission(`${resource}:manage`)),
    canDelete: (resource) => !isViewOnly && (permissionScope.hasPermission(`${resource}:delete`) || permissionScope.hasPermission(`${resource}:manage`)),
    canWrite: (resource) => !isViewOnly && (permissionScope.hasPermission(`${resource}:create`) || permissionScope.hasPermission(`${resource}:update`) || permissionScope.hasPermission(`${resource}:manage`)),
    loading,
    login,
    loginWithSSO,
    verify2FA,
    logout,
    refreshMe,
    hasPermission,
    hasAnyPermission,
    isAuthenticated: !!user,
    enabledModules,
    isModuleEnabled,
    tenantScope,
    licenseScope,
    moduleScope: { enabledModules, isModuleEnabled },
    roleScope,
    permissionScope: { hasPermission, hasAnyPermission },
    dataScope,
    supportSessionTenant,
    startSupportSession,
    endSupportSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useTenantScope = () => useAuth().tenantScope;
export const useLicenseScope = () => useAuth().licenseScope;
export const useModuleScope = () => useAuth().moduleScope;
export const useRoleScope = () => useAuth().roleScope;
export const usePermissionScope = () => useAuth().permissionScope;
export const useDataScope = () => useAuth().dataScope;

export default AuthContext;
